"use client";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BASE_URL } from "../config/config";
import { useAuth } from "../context/AuthContext";

function SignUp() {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [formData, setFormData] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  const [errors, setErrors] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });

  const [formError, setFormError] = React.useState(""); 
  const [showModal, setShowModal] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Form validation
  const validateForm = () => {
    const newErrors = { firstName: "", lastName: "", email: "", password: "" };

    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    else if (formData.firstName.trim().length < 2) newErrors.firstName = "At least 2 characters";

    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    else if (formData.lastName.trim().length < 2) newErrors.lastName = "At least 2 characters";

    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!emailRegex.test(formData.email)) newErrors.email = "Enter a valid email";

    if (!formData.password.trim()) newErrors.password = "Password is required";
    else if (formData.password.length < 6) newErrors.password = "At least 6 characters";

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: "" }));
    if (formError && field === "email") setFormError(""); 
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setFormError("");

    try {
      const response = await fetch(`${BASE_URL}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 201) {
        setShowModal(true);
        setFormData({ firstName: "", lastName: "", email: "", password: "" });
      } else {
        if (data.messages.error?.toLowerCase().includes("email")) {
          setFormError("Email already exists");
        } else {
          setFormError(data.messages.error || "Registration failed");
        }
      }
    } catch (err) {
      setFormError("An error occurred. Please try again.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignIn = () => navigate("/login");
  const handleReturnToStore = () => navigate("/");
  const closeModal = () => {
    setShowModal(false);
    navigate("/pendingverify");
  };

  const underlineVariants = {
    initial: { scaleX: 0 },
    hover: { scaleX: 1, transition: { duration: 0.3, ease: "easeInOut" } },
    exit: { scaleX: 0, transition: { duration: 0.3, ease: "easeInOut" } },
  };

  const AnimatedLink = ({ children, onClick, ariaLabel }) => (
    <motion.button
      onClick={onClick}
      className="relative text-base cursor-pointer text-yellow-500 bg-transparent border-none p-0 hover:opacity-80 outline-none"
      aria-label={ariaLabel}
      whileHover="hover"
      initial="initial"
      animate="initial"
    >
      <span className="relative z-10">{children}</span>
      <motion.span
        className="absolute bottom-0 left-0 w-full h-0.5 bg-yellow-400 origin-center"
        variants={underlineVariants}
      />
    </motion.button>
  );

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="flex flex-col items-center gap-8 p-10 bg-white rounded-3xl shadow-lg max-w-md w-full">
          {/* Header */}
          <header className="text-center">
            <h1 className="text-4xl font-bold text-yellow-500">Create Account</h1>
            <p className="mt-2 text-gray-600 text-sm">Sign up to get started</p>
          </header>

          {/* Sign In Link */}
          <div className="flex justify-center gap-2 text-gray-700 text-sm">
            <span>Already have an account?</span>
            <AnimatedLink onClick={handleSignIn} ariaLabel="Sign in">
              Sign in here
            </AnimatedLink>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-5 w-full" noValidate>
            {formError && (
              <div className="text-red-500 text-sm text-center mb-2">
                {formError}
              </div>
            )}

            {["firstName", "lastName", "email", "password"].map((field) => (
              <div key={field} className="flex flex-col">
                <input
                  type={field === "password" ? "password" : "text"}
                  value={formData[field]}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  placeholder={
                    field === "firstName"
                      ? "First Name"
                      : field === "lastName"
                      ? "Last Name"
                      : field === "email"
                      ? "Email"
                      : "Password"
                  }
                  className={`px-4 py-3 rounded-lg border transition-colors outline-none ${
                    errors[field] ? "border-red-500" : "border-yellow-300"
                  } focus:ring-2 focus:ring-yellow-300`}
                  required
                />
                {errors[field] && (
                  <span className="text-red-500 text-sm mt-1" role="alert">{errors[field]}</span>
                )}
              </div>
            ))}

            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-3 bg-yellow-400 hover:bg-yellow-500 text-white font-semibold rounded-lg shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Create account"
            >
              {isSubmitting ? "Creating..." : "Sign Up"}
            </button>
          </form>

          <div className="flex justify-center pt-2">
            <AnimatedLink onClick={handleReturnToStore} ariaLabel="Return to store">
              Return to Store
            </AnimatedLink>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showModal && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={closeModal}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                <svg
                  className="h-6 w-6 text-yellow-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-yellow-500 mb-2">Account Created!</h2>
              <p className="text-gray-600 mb-6">We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.
</p>
              <button
                onClick={closeModal}
                className="w-full px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-white rounded-lg shadow transition-all"
              >
                Continue
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}

export default SignUp;
  